/**
 * GraphQL Service for Virtual Warehouse Dashboard
 * Provides type-safe queries for inventory and sales momentum data
 */

const GRAPHQL_URL = '/graphql';

// Types matching the GraphQL schema
export type MomentumLevel = 'HOT' | 'WARM' | 'COOLING' | 'COLD' | 'BREAKOUT';
export type StockStatus = 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK' | 'PENDING_RESTOCK' | 'DISCONTINUED';
export type InventoryTrend = 'INCREASING' | 'DECREASING' | 'STABLE' | 'CRITICAL_LOW' | 'OVERSTOCKED';

export interface CategoryMetrics {
  categoryName: string;
  productCount: number;
  totalQuantity: number;
  totalValue: string;
  sold7d: number;
  sold30d: number;
  velocityDaily: number;
  momentum: MomentumLevel;
  momentumScore: number;
}

export interface WarehouseSummary {
  generatedAt: string;
  lastSync: string | null;
  totalProducts: number;
  totalQuantity: number;
  totalValue: string;
  estimatedQuantity: number;
  pendingPicks: number;
  pendingQuantity: number;
  lastPickValidated: string | null;
  inStockCount: number;
  lowStockCount: number;
  outOfStockCount: number;
  ordersToday: number;
  revenueToday: string;
  orders7d: number;
  revenue7d: string;
  unitsSold7d: number;
  orders30d: number;
  revenue30d: string;
  unitsSold30d: number;
  hotProducts: number;
  warmProducts: number;
  coldProducts: number;
  breakoutProducts: number;
  categories: CategoryMetrics[] | null;
}

export interface WarehouseProduct {
  productId: number;
  name: string;
  sku: string | null;
  barcode: string | null;
  category: string | null;
  qtyAvailable: number;
  qtyReserved: number;
  qtyEstimated: number;
  costPrice: string | null;
  listPrice: string | null;
  ebayAvgPrice: string | null;
  marketPrice: string | null;
  cardnadoT1Price: string | null;
  pokeUnlimitedPricelist: string | null;
  marginPercent: number | null;
  inventoryValue: string | null;
  stockStatus: StockStatus;
  daysOfStock: number | null;
  sold7d: number;
  sold30d: number;
  sold90d: number;
  velocityDaily: number;
  momentum: MomentumLevel;
  momentumScore: number;
  trend: InventoryTrend;
  pendingPicks: number;
  pendingPickNames: string[] | null;
  lastUpdated: string | null;
  imageUrl: string | null;
}

export interface WarehouseConnection {
  edges: WarehouseProduct[];
  totalCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface InventorySnapshot {
  snapshotDate: string;
  totalProducts: number;
  totalQuantity: number;
  totalValue: string;
  qtyChange: number;
  valueChange: string;
}

export interface SalesSnapshot {
  snapshotDate: string;
  totalOrders: number;
  totalRevenue: string;
  averageOrderValue: string;
  invoicedOrders: number;
  pendingOrders: number;
  invoicedRevenue: string;
  pendingRevenue: string;
}

export interface TopMover {
  rank: number;
  productId: number;
  productName: string;
  category: string | null;
  unitsSold: number;
  revenue: string;
  qtyAvailable: number;
  daysOfStock: number | null;
  momentum: MomentumLevel;
  velocityDaily: number;
}

export interface SalesMomentum {
  productId: number;
  productName: string;
  velocity7d: number;
  velocity30d: number;
  velocity90d: number;
  acceleration: number;
  momentum: MomentumLevel;
  momentumScore: number;
  dailySales: number[] | null;
  weeklySales: number[] | null;
  forecast7d: number | null;
  forecast30d: number | null;
  stockRunwayDays: number | null;
}

export interface InventoryAlert {
  alertId: string;
  severity: string;
  alertType: string;
  productId: number | null;
  productName: string | null;
  message: string;
  createdAt: string;
  acknowledged: boolean;
}

export interface ReconciliationProduct {
  productId: number;
  productName: string;
  odooQty: number;
  pendingQty: number;
  estimatedQty: number;
  discrepancy: number;
  pendingPicks: string[];
}

export interface ReconciliationReport {
  timestamp: string;
  lastPickValidated: string | null;
  lastPickName: string | null;
  pendingPicksCount: number;
  productsAffected: number;
  totalOdooQty: number;
  totalPendingQty: number;
  totalEstimatedQty: number;
  totalDiscrepancy: number;
  products: ReconciliationProduct[];
}

// Geo Distribution Types
export interface GeoLocation {
  city: string;
  state: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
}

export interface OrderDestination {
  orderId: number;
  orderName: string;
  orderDate: string;
  customerName: string;
  amountTotal: string;
  location: GeoLocation;
  products: string[] | null;
}

export interface StateDistribution {
  stateCode: string;
  stateName: string;
  country: string;
  orderCount: number;
  totalRevenue: string;
  avgOrderValue: string;
  topProducts: string[] | null;
}

export interface CountryDistribution {
  countryCode: string;
  countryName: string;
  orderCount: number;
  totalRevenue: string;
  states: StateDistribution[] | null;
}

export interface GeoDistributionSummary {
  generatedAt: string;
  periodDays: number;
  totalOrders: number;
  totalRevenue: string;
  uniqueStates: number;
  uniqueCountries: number;
  topStates: StateDistribution[];
  topCountries: CountryDistribution[];
  recentOrders: OrderDestination[];
}

// GraphQL query helper
async function graphqlQuery<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
  const response = await fetch(GRAPHQL_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  });

  if (!response.ok) {
    throw new Error(`GraphQL request failed: ${response.statusText}`);
  }

  const result = await response.json();

  if (result.errors) {
    throw new Error(result.errors.map((e: { message: string }) => e.message).join(', '));
  }

  return result.data;
}

// Warehouse Dashboard Queries
export const warehouseGraphQL = {
  /**
   * Get warehouse summary with all key metrics
   */
  async getSummary(): Promise<WarehouseSummary> {
    const query = `
      query WarehouseSummary {
        warehouseSummary {
          generatedAt
          lastSync
          totalProducts
          totalQuantity
          totalValue
          estimatedQuantity
          pendingPicks
          pendingQuantity
          lastPickValidated
          inStockCount
          lowStockCount
          outOfStockCount
          ordersToday
          revenueToday
          orders7d
          revenue7d
          unitsSold7d
          orders30d
          revenue30d
          unitsSold30d
          hotProducts
          warmProducts
          coldProducts
          breakoutProducts
          categories {
            categoryName
            productCount
            totalQuantity
            totalValue
            sold7d
            sold30d
            velocityDaily
            momentum
            momentumScore
          }
        }
      }
    `;

    const result = await graphqlQuery<{ warehouseSummary: WarehouseSummary }>(query);
    return result.warehouseSummary;
  },

  /**
   * Get paginated warehouse products
   */
  async getProducts(
    limit = 50,
    offset = 0,
    filters?: {
      search?: string;
      category?: string;
      stockStatus?: StockStatus;
      momentum?: MomentumLevel;
      minQuantity?: number;
      maxQuantity?: number;
    }
  ): Promise<WarehouseConnection> {
    const query = `
      query WarehouseProducts($limit: Int!, $offset: Int!, $filters: WarehouseFilterInput) {
        warehouseProducts(limit: $limit, offset: $offset, filters: $filters) {
          edges {
            productId
            name
            sku
            barcode
            category
            qtyAvailable
            qtyReserved
            qtyEstimated
            costPrice
            listPrice
            ebayAvgPrice
            marketPrice
            cardnadoT1Price
            pokeUnlimitedPricelist
            marginPercent
            inventoryValue
            stockStatus
            daysOfStock
            sold7d
            sold30d
            sold90d
            velocityDaily
            momentum
            momentumScore
            trend
            pendingPicks
            pendingPickNames
            lastUpdated
            imageUrl
          }
          totalCount
          hasNextPage
          hasPreviousPage
        }
      }
    `;

    const result = await graphqlQuery<{ warehouseProducts: WarehouseConnection }>(query, {
      limit,
      offset,
      filters,
    });
    return result.warehouseProducts;
  },

  /**
   * Get inventory snapshots for charts
   */
  async getInventorySnapshots(limit = 30, startDate?: string, endDate?: string): Promise<InventorySnapshot[]> {
    const query = `
      query InventorySnapshots($limit: Int!, $startDate: Date, $endDate: Date) {
        inventorySnapshots(limit: $limit, startDate: $startDate, endDate: $endDate) {
          snapshotDate
          totalProducts
          totalQuantity
          totalValue
          qtyChange
          valueChange
        }
      }
    `;

    const result = await graphqlQuery<{ inventorySnapshots: InventorySnapshot[] }>(query, {
      limit,
      startDate,
      endDate,
    });
    return result.inventorySnapshots;
  },

  /**
   * Get sales snapshots for charts
   */
  async getSalesSnapshots(limit = 30, startDate?: string, endDate?: string): Promise<SalesSnapshot[]> {
    const query = `
      query SalesSnapshots($limit: Int!, $startDate: Date, $endDate: Date) {
        salesSnapshots(limit: $limit, startDate: $startDate, endDate: $endDate) {
          snapshotDate
          totalOrders
          totalRevenue
          averageOrderValue
          invoicedOrders
          pendingOrders
          invoicedRevenue
          pendingRevenue
        }
      }
    `;

    const result = await graphqlQuery<{ salesSnapshots: SalesSnapshot[] }>(query, {
      limit,
      startDate,
      endDate,
    });
    return result.salesSnapshots;
  },

  /**
   * Get top moving products
   */
  async getTopMovers(periodDays = 7, limit = 20): Promise<TopMover[]> {
    const query = `
      query TopMovers($periodDays: Int!, $limit: Int!) {
        topMovers(periodDays: $periodDays, limit: $limit) {
          rank
          productId
          productName
          category
          unitsSold
          revenue
          qtyAvailable
          daysOfStock
          momentum
          velocityDaily
        }
      }
    `;

    const result = await graphqlQuery<{ topMovers: TopMover[] }>(query, {
      periodDays,
      limit,
    });
    return result.topMovers;
  },

  /**
   * Get sales momentum analysis
   */
  async getSalesMomentum(
    limit = 50,
    filters?: {
      minVelocity?: number;
      momentumLevels?: MomentumLevel[];
      category?: string;
    }
  ): Promise<SalesMomentum[]> {
    const query = `
      query SalesMomentum($limit: Int!, $minVelocity: Float, $momentumLevels: [MomentumLevel!], $category: String) {
        salesMomentum(limit: $limit, minVelocity: $minVelocity, momentumLevels: $momentumLevels, category: $category) {
          productId
          productName
          velocity7d
          velocity30d
          velocity90d
          acceleration
          momentum
          momentumScore
          forecast7d
          forecast30d
          stockRunwayDays
        }
      }
    `;

    const result = await graphqlQuery<{ salesMomentum: SalesMomentum[] }>(query, {
      limit,
      minVelocity: filters?.minVelocity,
      momentumLevels: filters?.momentumLevels,
      category: filters?.category,
    });
    return result.salesMomentum;
  },

  /**
   * Get inventory alerts
   */
  async getAlerts(limit = 50, acknowledged?: boolean): Promise<InventoryAlert[]> {
    const query = `
      query InventoryAlerts($limit: Int!, $acknowledged: Boolean) {
        inventoryAlerts(limit: $limit, acknowledged: $acknowledged) {
          alertId
          severity
          alertType
          productId
          productName
          message
          createdAt
          acknowledged
        }
      }
    `;

    const result = await graphqlQuery<{ inventoryAlerts: InventoryAlert[] }>(query, {
      limit,
      acknowledged,
    });
    return result.inventoryAlerts;
  },

  /**
   * Get full reconciliation report showing pending picks impact
   */
  async getReconciliationReport(): Promise<ReconciliationReport> {
    const query = `
      query ReconciliationReport {
        reconciliationReport {
          timestamp
          lastPickValidated
          lastPickName
          pendingPicksCount
          productsAffected
          totalOdooQty
          totalPendingQty
          totalEstimatedQty
          totalDiscrepancy
          products {
            productId
            productName
            odooQty
            pendingQty
            estimatedQty
            discrepancy
            pendingPicks
          }
        }
      }
    `;

    const result = await graphqlQuery<{ reconciliationReport: ReconciliationReport }>(query);
    return result.reconciliationReport;
  },

  /**
   * Get geographic distribution of orders based on shipping addresses
   */
  async getGeoDistribution(periodDays: number = 30, limit: number = 50): Promise<GeoDistributionSummary> {
    const query = `
      query GeoDistribution($periodDays: Int!, $limit: Int!) {
        geoDistribution(periodDays: $periodDays, limit: $limit) {
          generatedAt
          periodDays
          totalOrders
          totalRevenue
          uniqueStates
          uniqueCountries
          topStates {
            stateCode
            stateName
            country
            orderCount
            totalRevenue
            avgOrderValue
            topProducts
          }
          topCountries {
            countryCode
            countryName
            orderCount
            totalRevenue
          }
          recentOrders {
            orderId
            orderName
            orderDate
            customerName
            amountTotal
            location {
              city
              state
              country
              latitude
              longitude
            }
            products
          }
        }
      }
    `;

    const result = await graphqlQuery<{ geoDistribution: GeoDistributionSummary }>(query, {
      periodDays,
      limit,
    });
    return result.geoDistribution;
  },

  /**
   * Get stock levels for chart display (replacement for Odoo API getStockLevels)
   * Fetches products sorted by quantity or value, in ascending or descending order
   */
  async getStockLevels(
    sortBy: 'quantity' | 'value' = 'quantity',
    viewMode: 'top' | 'bottom' = 'top',
    limit: number = 10
  ): Promise<{
    products: Array<{
      id: number;
      name: string;
      qty_available: number;
      inventory_value: number;
      unit_price: number;
      listing_price?: number;
      ebay_avg_price?: number;
      cardnado_t1_price?: number;
      poke_unlimited_pricelist?: number;
      has_market_data: boolean;
    }>;
    total_count: number;
    generated_at: string;
  }> {
    // Use getProducts and apply local sorting
    // Fetch more products to ensure we have enough data for sorting
    const fetchLimit = Math.max(limit * 10, 100);
    
    const response = await this.getProducts(fetchLimit, 0);
    
    // Sort by the requested field
    let sortedProducts = [...response.edges];
    
    if (sortBy === 'quantity') {
      sortedProducts.sort((a, b) => 
        viewMode === 'top' 
          ? b.qtyAvailable - a.qtyAvailable 
          : a.qtyAvailable - b.qtyAvailable
      );
    } else {
      // Sort by value (inventory value)
      sortedProducts.sort((a, b) => {
        const aValue = parseFloat(a.inventoryValue || '0');
        const bValue = parseFloat(b.inventoryValue || '0');
        return viewMode === 'top' ? bValue - aValue : aValue - bValue;
      });
    }
    
    // Take the top/bottom N
    const selectedProducts = sortedProducts.slice(0, limit);
    
    // Transform to the expected format (matching odooApiService.getStockLevels response)
    const products = selectedProducts.map(p => ({
      id: p.productId,
      name: p.name,
      qty_available: p.qtyAvailable,
      inventory_value: parseFloat(p.inventoryValue || '0'),
      unit_price: parseFloat(p.costPrice || p.listPrice || '0'),
      listing_price: p.listPrice ? parseFloat(p.listPrice) : undefined,
      ebay_avg_price: p.ebayAvgPrice ? parseFloat(p.ebayAvgPrice) : undefined,
      cardnado_t1_price: p.cardnadoT1Price ? parseFloat(p.cardnadoT1Price) : undefined,
      poke_unlimited_pricelist: p.pokeUnlimitedPricelist ? parseFloat(p.pokeUnlimitedPricelist) : undefined,
      has_market_data: !!p.ebayAvgPrice || !!p.marketPrice || !!p.cardnadoT1Price || !!p.pokeUnlimitedPricelist
    }));
    
    return {
      products,
      total_count: response.totalCount,
      generated_at: new Date().toISOString()
    };
  },
};

export default warehouseGraphQL;
